import React, { useState, useEffect } from 'react';
import { Building2, Save, Percent } from 'lucide-react';
import { useFormOptions } from '../../../config/constants';
import { useConfiguracionModel } from '../../../modelos/Configuracion/modelo_configuracion';

const ConfiguracionServiciosEmpresas = ({ configuracion }) => {
  const { empresasList, tipoServiciosListAdmin } = useFormOptions();
  const { updateConfiguracion } = useConfiguracionModel();

  // El estado ahora es un objeto que puede contener { empresa, referido }
  const [asignaciones, setAsignaciones] = useState({});
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);

  // Inicializar asignaciones, adaptando la estructura de datos si es necesario
  useEffect(() => {
    if (configuracion?.servicios_empresas) {
      const initialState = {};
      Object.keys(configuracion.servicios_empresas).forEach(servicio => {
        const valor = configuracion.servicios_empresas[servicio];
        // Comprueba si el dato guardado es solo un string (formato antiguo)
        if (typeof valor === 'string') {
          initialState[servicio] = { empresa: valor, referido: 0 };
        } else if (typeof valor === 'object' && valor !== null) {
          // Si ya es un objeto (formato nuevo), lo utiliza
          initialState[servicio] = { empresa: valor.empresa || '', referido: valor.referido || 0 };
        }
      });
      setAsignaciones(initialState);
    }
    setLoading(false);
  }, [configuracion]);

  /**
   * Maneja los cambios tanto en la selección de empresa como en el input de referido.
   * @param {string} tipoServicio - El servicio que se está modificando.
   * @param {'empresa' | 'referido'} campo - El campo que cambia.
   * @param {string | number} valor - El nuevo valor.
   */
  const handleAsignacionChange = (tipoServicio, campo, valor) => {
    setAsignaciones(prev => {
      const asignacionActual = prev[tipoServicio] || { empresa: '', referido: 0 };
      const nuevaAsignacion = { ...asignacionActual, [campo]: valor };
      
      // Si se cambia la empresa a la nuestra o se deselecciona, el referido se resetea a 0.
      if (campo === 'empresa' && (valor === configuracion.nombreEmpresa || valor === '')) {
        nuevaAsignacion.referido = 0;
      }

      // Elimina la asignación si la empresa se deselecciona
      if (campo === 'empresa' && valor === '') {
        const nuevasAsignaciones = { ...prev };
        delete nuevasAsignaciones[tipoServicio];
        return nuevasAsignaciones;
      }

      return { ...prev, [tipoServicio]: nuevaAsignacion };
    });
  };

  /**
   * Filtra y guarda las asignaciones que tienen una empresa seleccionada.
   */
  const handleGuardar = async () => {
    setGuardando(true);
    
    const asignacionesParaGuardar = Object.entries(asignaciones)
      .filter(([_, value]) => value && value.empresa) // Solo guardar si hay una empresa
      .reduce((acc, [key, value]) => {
        acc[key] = {
          empresa: value.empresa,
          referido: Number(value.referido) || 0 // Asegura que el referido sea un número
        };
        return acc;
      }, {});
      
    console.log("A guardar las asignaciones:", asignacionesParaGuardar);
    await updateConfiguracion(configuracion.nombreEmpresa,'servicios_empresas',asignacionesParaGuardar,'field','CONFIGURACION');
    setGuardando(false);
  };

  if (loading) {
    return (
      <div className="bg-white p-6 rounded-lg border">
        <div className="flex items-center space-x-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
          <span>Cargando configuración...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white p-6 rounded-lg border">
      <h3 className="text-lg font-semibold mb-4 flex items-center">
        <Building2 className="mr-2 h-5 w-5" />
        Configuración de Servicios por Empresa
      </h3>

      {Object.keys(asignaciones).length > 0 ? (
        <span style={{color: 'green'}}>✅ Configurado ({Object.keys(asignaciones).length})</span>
      ) : (
        <span style={{color: 'red'}}>❌ Debe llenar</span>
      )}
      
      <div className="space-y-4 mt-4">
        {tipoServiciosListAdmin.map(tipoServicio => {
          const asignacion = asignaciones[tipoServicio] || { empresa: '', referido: 0 };
          const esReferidoDeshabilitado = !asignacion.empresa || asignacion.empresa === configuracion.nombreEmpresa;

          return (
            <div key={tipoServicio} className="grid grid-cols-1 md:grid-cols-5 items-center gap-4 p-3 border rounded-lg bg-gray-50/50">
              <span className="font-medium md:col-span-2">{tipoServicio}</span>
              
              <div className="md:col-span-2">
                <select
                  value={asignacion.empresa}
                  onChange={(e) => handleAsignacionChange(tipoServicio, 'empresa', e.target.value)}
                  className="w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Seleccionar empresa</option>
                  {empresasList.map(empresa => (
                    <option key={empresa} value={empresa}>
                      {empresa}
                    </option>
                  ))}
                </select>
              </div>

              <div className="relative md:col-span-1">
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={asignacion.referido}
                  onChange={(e) => handleAsignacionChange(tipoServicio, 'referido', e.target.value)}
                  disabled={esReferidoDeshabilitado}
                  className="w-full pl-3 pr-8 py-2 border rounded-md disabled:bg-gray-200 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="% referido"
                />
                <Percent className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 h-4 w-4" />
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6">
        <button
          onClick={handleGuardar}
          disabled={guardando}
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          <Save className="h-4 w-4" />
          <span>{guardando ? 'Guardando...' : 'Guardar Configuración'}</span>
        </button>
      </div>
    </div>
  );
};

export default ConfiguracionServiciosEmpresas;

